import React, { useEffect, useRef, useState } from "react";

import type { MeetingStatus } from "@performyard/models";
import { ButtonStandard, Checkbox, ModalBase, Text } from "@performyard/py-components";

import { REMOVE_CALENDAR_CONFIG, REMOVE_VIDEO_CONFERENCING_CONFIG, STATUS_CONFIRMATION_CONFIG } from "../config";

export type StatusConfirmationModalConfirmOptions = {
	removeFromCalendar?: boolean;
	removeVideoConferencing?: boolean;
};

type StatusConfirmationModalProps = {
	isOpen: boolean;
	isLoading: boolean;
	status: MeetingStatus | null;
	action?: "status-change" | "remove-from-calendar" | "remove-video-conferencing";
	onClose: () => void;
	onConfirm: (options: StatusConfirmationModalConfirmOptions) => void;
	hasConnectedCalendarEvent?: boolean;
	hasConnectedVideoConferencing?: boolean;
};

/**
 * Confirmation modal for status changes and calendar/VC removal.
 * Shows appropriate message based on the action type.
 */
const StatusConfirmationModal = ({
	isOpen,
	isLoading,
	status,
	action = "status-change",
	onClose,
	onConfirm,
	hasConnectedCalendarEvent = false,
	hasConnectedVideoConferencing = false,
}: StatusConfirmationModalProps): React.ReactElement => {
	const [removeFromCalendar, setRemoveFromCalendar] = useState(false);
	const [removeVideoConferencing, setRemoveVideoConferencing] = useState(false);

	const contentRef = useRef<{
		config: {
			title: string;
			message: string;
			confirmText: string;
			confirmButtonColor: "primary" | "red";
		} | null;
		showCalendarCheckbox: boolean;
		showVideoConferencingCheckbox: boolean;
	}>({
		config: null,
		showCalendarCheckbox: false,
		showVideoConferencingCheckbox: false,
	});

	const getActionConfig = (
		actionType: "status-change" | "remove-from-calendar" | "remove-video-conferencing",
		statusValue: MeetingStatus | null
	): {
		title: string;
		message: string;
		confirmText: string;
		confirmButtonColor: "primary" | "red";
	} | null => {
		if (actionType === "remove-from-calendar") {
			return REMOVE_CALENDAR_CONFIG;
		}
		if (actionType === "remove-video-conferencing") {
			return REMOVE_VIDEO_CONFERENCING_CONFIG;
		}

		if (statusValue && statusValue in STATUS_CONFIRMATION_CONFIG) {
			return STATUS_CONFIRMATION_CONFIG[statusValue as keyof typeof STATUS_CONFIRMATION_CONFIG];
		}

		return null;
	};

	useEffect(() => {
		if (isOpen) {
			const modalConfig = getActionConfig(action, status);
			const isCancelStatusChange = action === "status-change" && status === "canceled";
			contentRef.current = {
				config: modalConfig,
				showCalendarCheckbox: isCancelStatusChange && hasConnectedCalendarEvent,
				showVideoConferencingCheckbox: isCancelStatusChange && hasConnectedVideoConferencing,
			};
		}
	}, [isOpen, action, status, hasConnectedCalendarEvent, hasConnectedVideoConferencing]);

	const modalConfig = isOpen ? getActionConfig(action, status) : contentRef.current.config;
	const isCancelStatusChange = action === "status-change" && status === "canceled";
	const showCalendarCheckbox = isOpen
		? isCancelStatusChange && hasConnectedCalendarEvent
		: contentRef.current.showCalendarCheckbox;
	const showVideoConferencingCheckbox = isOpen
		? isCancelStatusChange && hasConnectedVideoConferencing
		: contentRef.current.showVideoConferencingCheckbox;

	const handleConfirm = (): void => {
		onConfirm({ removeFromCalendar, removeVideoConferencing });
	};

	const handleClose = (): void => {
		setRemoveFromCalendar(false);
		setRemoveVideoConferencing(false);
		onClose();
	};

	return (
		<ModalBase isOpen={isOpen} onClose={isLoading ? () => {} : handleClose} dangerouslySetClassName="w-[432px]">
			<div className="flex flex-col items-center gap-4 px-6 pb-6 text-center">
				<div className="flex flex-col items-center">
					<Text size="sub-heading" weight="semi-bold">
						{modalConfig?.title}
					</Text>
					<Text size="paragraph-2" weight="regular">
						{modalConfig?.message}
					</Text>
				</div>
				{(showCalendarCheckbox || showVideoConferencingCheckbox) && (
					<div className="flex flex-col items-center gap-2">
						{showCalendarCheckbox && (
							<div className="flex items-center gap-2">
								<Checkbox
									id="remove-from-calendar"
									checked={removeFromCalendar}
									onCheckedChange={setRemoveFromCalendar}
									disabled={isLoading}
									aria-label="Remove from My Calendar"
								/>
								<Text size="paragraph-3" weight="regular">
									Remove from My Calendar
								</Text>
							</div>
						)}
						{showVideoConferencingCheckbox && (
							<div className="flex items-center gap-2">
								<Checkbox
									id="remove-video-conferencing"
									checked={removeVideoConferencing}
									onCheckedChange={setRemoveVideoConferencing}
									disabled={isLoading}
									aria-label="Remove Video Conferencing"
								/>
								<Text size="paragraph-3" weight="regular">
									Remove Video Conferencing
								</Text>
							</div>
						)}
					</div>
				)}
			</div>

			<div className="flex justify-center gap-2 px-6 pb-6">
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={handleClose}
						disabled={isLoading}
					>
						Cancel
					</ButtonStandard>
				</div>
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="solid"
						color={modalConfig?.confirmButtonColor}
						size="large"
						onClick={handleConfirm}
						disabled={isLoading}
					>
						{modalConfig?.confirmText}
					</ButtonStandard>
				</div>
			</div>
		</ModalBase>
	);
};

export default StatusConfirmationModal;
